import { useCallback } from 'react';
import useTranslation from 'next-translate/useTranslation';

import Constants from '@/constants/const';
import { setCookie, isSayWeeeApp } from '@/utils/tool';
import { setLanguage } from '@/api/customer';
import { getLoginState } from '@/utils/business';
import callAppFunction from '@/utils/bridge';
import useClickActionT2 from '@/tracks/useClickActionT2';
import useGlobalNavHeader from '@/hooks/globalnavheader/provider';
import { deleteCookie } from '@/utils/cookie';
import { setStoreLanguage } from '@/api/item';

// 转换为接口语言
const toApiLang = (lng: string) => (lng === Constants.LANGUAGE_CHINESE_T ? Constants.LANGUAGE_CHINESE_T_ALIAS : lng);

const isSameLanguage = (a: string, b: string) => toApiLang(a) === toApiLang(b);

/**
 * useLanguageSwitcher 配置选项
 */
interface UseLanguageSwitcherOptions {
  /** 点击埋点回调 */
  clickTrack?: () => void;
  /** 选择语言后的埋点回调 */
  chooseLanguageTrack?: (lang: string) => void;
  /** 发送埋点前的参数处理 */
  onBeforeSendTrack?: (params: ObjectType) => ObjectType;
}

/**
 * 封装语言切换逻辑的 Hook
 */
export const useLanguageSwitcher = (options: UseLanguageSwitcherOptions = {}) => {
  const { clickTrack, chooseLanguageTrack, onBeforeSendTrack } = options;
  const { lang } = useTranslation('common');
  const globalNavHeader = useGlobalNavHeader();
  const languages = globalNavHeader?.supportLanguages || [];

  const { buttonClickView } = useClickActionT2();

  /**
   * 处理埋点逻辑
   */
  const handleTracking = useCallback(
    (lng: string) => {
      if (chooseLanguageTrack) {
        chooseLanguageTrack(lng);
      } else {
        const params = {
          co: {
            target_nm: lng
          }
        };
        buttonClickView(onBeforeSendTrack ? onBeforeSendTrack(params) : params);
      }
    },
    [chooseLanguageTrack, onBeforeSendTrack, buttonClickView]
  );

  /**
   * 执行底层的语言切换操作
   */
  const executeSwitch = useCallback(
    async (oldLng: string, newLang: string) => {
      const targetLang = toApiLang(newLang);
      const previousLang = toApiLang(oldLng);

      await setStoreLanguage({
        previous_language: previousLang,
        language: targetLang
      });

      if (!isSameLanguage(newLang, lang)) {
        deleteCookie(Constants.WEEE_STORE);
        deleteCookie(Constants.STORE_INFO);
        setCookie(Constants.LANGUAGE, targetLang, {}, { maxAge: Constants.LANGUAGE_EXPIRED_TIME });

        if (isSayWeeeApp()) {
          callAppFunction('changeLanguage', { language: targetLang }, null);
          return;
        }

        const { pathname, search, origin } = window.location;
        const newPathname = pathname.replace(Constants.LANGUAGE_REGEXP_TWO, targetLang ? `/${targetLang}` : '/');
        // 移除grocery-store等参数
        const searchParams = new URLSearchParams(search);
        const paramsToRemove = ['grocery-store', 'set_lang'];
        paramsToRemove.forEach(param => {
          searchParams.delete(param);
        });
        const newSearch = searchParams.toString();

        const searchString = newSearch ? '?' + newSearch : '';
        const newUrl = `${origin}${newPathname}${searchString}`;
        window.location.replace(newUrl);
      }
    },
    [lang]
  );

  /**
   * 变更语言的入口函数
   */
  const changeLanguage = useCallback(
    (newLang: string) => {
      // 触发点击埋点
      clickTrack?.();

      if (!isSameLanguage(newLang, lang)) {
        handleTracking(toApiLang(newLang));

        const isLogin = getLoginState();

        if (isLogin) {
          setLanguage(toApiLang(newLang))
            .then(({ result }) => {
              if (result) {
                void executeSwitch(lang, newLang).catch(() => {});
              }
            })
            .catch(() => {});
        } else {
          void executeSwitch(lang, newLang).catch(() => {});
        }
      }
    },
    [lang, clickTrack, handleTracking, executeSwitch]
  );

  /**
   * 适配 select 组件的 change 事件处理函数
   */
  const onSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      changeLanguage(e.target.value);
    },
    [changeLanguage]
  );

  // 找到当前选中的语言详情
  const currentLanguage = languages.find(item => item.key === lang);

  return {
    /** 当前语言标识 (如 'zh', 'en') */
    lang,
    /** 支持的语言列表 */
    languages,
    /** 当前语言的详细信息 (包含 label 等) */
    currentLanguage,
    /** 变更语言函数 (直接传入语言标识) */
    changeLanguage,
    /** 适配 select 元素的变更处理器 */
    onSelectChange
  };
};
